/**
 * ============================================================================
 * SupplierKpiDashboard — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /suppliers/kpi-dashboard
 * Purpose: cross-supplier KPI grid (8 KPIs + aggregate score + risk badge).
 *          Sortable on every column. CSV export emits a DASHBOARD_EXPORTED
 *          audit row server-side (QMD-EXFIL-001).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Filters live at the top, mirroring the workbench so users keep one
 *     mental model.
 *   - Export button has both a label and an aria-label that explains the
 *     audit-logging side-effect.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero, EmptyState } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierKpiDashboard.module.css';
import { KpiTable } from './KpiTable';
import { SupplierBenchmarkPanel } from './SupplierBenchmarkPanel';
import {
  useKpiDashboardQuery,
  type KpiDashboardParams,
} from './useKpiDashboardQuery';
import { useExportKpiMutation } from './useExportKpiMutation';
import { downloadTextFile } from '../../api/client';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
} from '../SupplierWorkbench/useSuppliersQuery';

const CRIT: SupplierCriticality[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];
const RISK: RiskCategory[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];
const LIFE: SupplierLifecycleStatus[] = [
  'PENDING_REQUEST',
  'QUESTIONNAIRE_ISSUED',
  'AUDIT_SCHEDULED',
  'ACTIVE',
  'RESTRICTED',
  'DISQUALIFIED',
  'ARCHIVED',
];

export interface SupplierKpiDashboardProps {
  onOpenSupplier?: (id: string) => void;
}

export function SupplierKpiDashboard({
  onOpenSupplier,
}: SupplierKpiDashboardProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useState<KpiDashboardParams>({});
  const [exportNote, setExportNote] = useState<string | null>(null);

  const dashQ = useKpiDashboardQuery(params);
  const exportM = useExportKpiMutation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const rows = dashQ.data?.rows ?? [];
  const generatedAtUtc = dashQ.data?.generated_at_utc ?? new Date().toISOString();

  function update<K extends keyof KpiDashboardParams>(
    key: K,
    next: KpiDashboardParams[K] | '',
  ) {
    setParams((prev) => {
      const merged = { ...prev };
      if (next === '' || next == null) {
        delete merged[key];
      } else {
        merged[key] = next as KpiDashboardParams[K];
      }
      return merged;
    });
  }

  function handleOpen(id: string) {
    if (onOpenSupplier) {
      onOpenSupplier(id);
      return;
    }
    navigate({ to: '/suppliers/$id', params: { id } });
  }

  async function exportCsv() {
    setExportNote(null);
    try {
      const res = await exportM.mutateAsync(params);
      // The CSV comes back inline (built + audited server-side). We blob-
      // download it locally — a new-tab download can't carry the Bearer
      // token, and the audit row is already written. (F0-DL.)
      downloadTextFile(res.csv, res.filename || 'supplier-kpi-dashboard.csv');
      setExportNote(
        t('supplier.kpi.exportSuccess', {
          defaultValue:
            'Exported {{rows}} suppliers. Audit event {{id}} recorded (DASHBOARD_EXPORTED).',
          rows: res.row_count,
          id: res.audit_event_id,
        }),
      );
    } catch (e: unknown) {
      setExportNote(
        e instanceof Error
          ? e.message
          : t('supplier.kpi.exportFailed', {
              defaultValue: 'Export failed.',
            }),
      );
    }
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('supplier.kpi.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('supplier.kpi.title', { defaultValue: 'Supplier KPI dashboard' })}
        subtitle={t('supplier.kpi.subtitle', {
          defaultValue:
            'Cross-supplier KPI grid for the Quality Director view. Click a supplier name to drill into its dossier.',
        })}
        actions={
          <Button
            variant="primary"
            onClick={() => void exportCsv()}
            isLoading={exportM.isPending}
            aria-label={t('supplier.kpi.exportAria', {
              defaultValue:
                'Export the current KPI grid to CSV — emits a DASHBOARD_EXPORTED audit event',
            })}
          >
            {t('supplier.kpi.export', { defaultValue: 'Export CSV' })}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className={styles.headerRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('supplier.kpi.gridTitle', {
                defaultValue: 'KPI grid',
              })}
            </h2>
            <div className={styles.right}>
              <form
                role="search"
                className={styles.filters}
                onSubmit={(e) => e.preventDefault()}
                aria-label={t('supplier.kpi.filtersAria', {
                  defaultValue: 'Supplier KPI filters',
                })}
              >
                <div className={styles.filterField}>
                  <label htmlFor="kpi-f-crit" className={styles.filterLabel}>
                    {t('supplier.workbench.filters.criticality', {
                      defaultValue: 'Criticality',
                    })}
                  </label>
                  <select
                    id="kpi-f-crit"
                    className={styles.filterSelect}
                    value={params.criticality ?? ''}
                    onChange={(e) =>
                      update(
                        'criticality',
                        e.target.value as SupplierCriticality | '',
                      )
                    }
                  >
                    <option value="">
                      {t('supplier.workbench.filters.all', {
                        defaultValue: 'All',
                      })}
                    </option>
                    {CRIT.map((c) => (
                      <option key={c} value={c}>
                        {t(`supplier.criticality.${c}`, { defaultValue: c })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterField}>
                  <label htmlFor="kpi-f-life" className={styles.filterLabel}>
                    {t('supplier.workbench.filters.lifecycle', {
                      defaultValue: 'Lifecycle status',
                    })}
                  </label>
                  <select
                    id="kpi-f-life"
                    className={styles.filterSelect}
                    value={params.lifecycle_status ?? ''}
                    onChange={(e) =>
                      update(
                        'lifecycle_status',
                        e.target.value as SupplierLifecycleStatus | '',
                      )
                    }
                  >
                    <option value="">
                      {t('supplier.workbench.filters.all', {
                        defaultValue: 'All',
                      })}
                    </option>
                    {LIFE.map((s) => (
                      <option key={s} value={s}>
                        {t(`supplier.lifecycle.${s}`, { defaultValue: s })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterField}>
                  <label htmlFor="kpi-f-risk" className={styles.filterLabel}>
                    {t('supplier.workbench.filters.risk', {
                      defaultValue: 'Risk category',
                    })}
                  </label>
                  <select
                    id="kpi-f-risk"
                    className={styles.filterSelect}
                    value={params.risk_category ?? ''}
                    onChange={(e) =>
                      update(
                        'risk_category',
                        e.target.value as RiskCategory | '',
                      )
                    }
                  >
                    <option value="">
                      {t('supplier.workbench.filters.all', {
                        defaultValue: 'All',
                      })}
                    </option>
                    {RISK.map((r) => (
                      <option key={r} value={r}>
                        {t(`supplier.risk.${r}`, { defaultValue: r })}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
              <span className={styles.generatedAt} aria-live="polite">
                {t('supplier.kpi.generatedAt', {
                  defaultValue: 'Generated {{ts}}',
                  ts: dateFmt.format(new Date(generatedAtUtc)),
                })}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {exportNote && (
            <div role="status" aria-live="polite" className={styles.exportNote}>
              {exportNote}
            </div>
          )}

          {dashQ.isLoading && (
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {dashQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('supplier.kpi.errors.loadFailed', {
                  defaultValue: 'Could not load supplier KPI grid.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => dashQ.refetch()}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!dashQ.isLoading && !dashQ.isError && rows.length === 0 && (
            <EmptyState
              icon="🏭"
              title={t('supplier.kpi.emptyTitle', { defaultValue: 'No suppliers match these filters' })}
              description={t('supplier.kpi.empty', {
                defaultValue: 'Clear the criticality / lifecycle / risk filters above, or add a supplier from the workbench to see KPI rows here.',
              })}
            />
          )}

          {!dashQ.isLoading && !dashQ.isError && rows.length > 0 && (
            <>
              <KpiTable rows={rows} onOpenSupplier={handleOpen} />
              <SupplierBenchmarkPanel rows={rows} />
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
